import { useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Copy, Download, ExternalLink } from "lucide-react";
import { FRAMES, SCRIPT_30, SCRIPT_90, VIDEO_BRIEF } from "@/lib/videoKit";
import { Button } from "@/components/ui/button";

function Scene({ scene }) {
  return (
    <div className="grid sm:grid-cols-[86px_1fr] gap-x-4 gap-y-1 py-3 border-b border-[#12141C] last:border-0" data-testid={`scene-${scene.id}`}>
      <div className="text-[10px] font-mono-tech text-slate-500 pt-0.5">
        <span className="text-[#7FC3FF]">{scene.id}</span><br className="hidden sm:block" /> {scene.time}
      </div>
      <div>
        <p className="text-sm text-slate-200 italic">“{scene.vo}”</p>
        <p className="text-xs text-slate-500 mt-1">{scene.screen}</p>
      </div>
    </div>
  );
}

export const StoryboardPanel = () => {
  const [cut, setCut] = useState("90");
  const [showBrief, setShowBrief] = useState(false);

  const copyBrief = () => {
    if (!navigator.clipboard?.writeText) { setShowBrief(true); toast.info("Brief opened below — select it and copy"); return; }
    navigator.clipboard.writeText(VIDEO_BRIEF)
      .then(() => toast.success("Video Agent brief copied — paste it in the Video Agent tab"))
      .catch(() => { setShowBrief(true); toast.info("Clipboard blocked — the brief is open below, select it and copy"); });
  };

  return (
    <section className="mt-6 rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5" data-testid="demo-storyboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold flex items-center gap-2"><Clapperboard className="h-4 w-4 text-[#0A8CF2]" /> Storyboard &amp; script</h2>
          <p className="text-xs text-slate-400 mt-1">Eight frames plus every voiceover line. Paste the brief into Emergent's Video Agent tab, attach these frames and your screen recordings.</p>
        </div>
        <Button onClick={copyBrief} data-testid="copy-video-brief-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
          <Copy className="mr-2 h-4 w-4" /> Copy the Video Agent brief
        </Button>
      </div>

      <button type="button" onClick={() => setShowBrief((s) => !s)} data-testid="toggle-video-brief" className="mt-2 text-xs text-slate-500 hover:text-white">
        {showBrief ? "hide the brief text" : "or show the brief text to copy by hand"}
      </button>
      {showBrief && (
        <textarea readOnly value={VIDEO_BRIEF} rows={10} data-testid="video-brief-text"
                  onFocus={(e) => e.target.select()}
                  className="w-full mt-2 rounded-lg border border-[#1C2233] bg-black/60 p-3 text-[11px] leading-relaxed text-slate-300 font-mono-tech outline-none" />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        {FRAMES.map((f) => (
          <div key={f.n} className="group rounded-lg border border-[#1C2233] bg-[#05070C] overflow-hidden" data-testid={`frame-${f.n}`}>
            <a href={f.url} target="_blank" rel="noreferrer" title="Open full size">
              <img src={f.url} alt={f.label} loading="lazy" className="h-24 w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            </a>
            <div className="p-2.5">
              <div className="text-xs font-semibold leading-tight">{f.n}. {f.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-600 font-mono-tech mt-1">{f.use}</div>
              <div className="flex items-center gap-3 mt-2">
                <a href={f.url} target="_blank" rel="noreferrer" data-testid={`frame-open-${f.n}`} className="text-[11px] text-[#7FC3FF] hover:text-white inline-flex items-center gap-1">open <ExternalLink className="h-3 w-3" /></a>
                <a href={f.url} download={`vinconex-frame-${f.n}.jpeg`} data-testid={`frame-download-${f.n}`} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1">save <Download className="h-3 w-3" /></a>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-6 mb-1">
        {[["90", "90-second hero"], ["30", "30-second outreach cut"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setCut(k)} data-testid={`script-tab-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${cut === k ? "bg-[#0A8CF2] text-white" : "border border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {cut === "90" ? (
        <div data-testid="script-90">
          {SCRIPT_90.map((s) => (
            <div key={s.section} className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-[#0A8CF2] font-mono-tech">{s.section}</div>
              <div className="mt-1">{s.scenes.map((c) => <Scene key={c.id} scene={c} />)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4" data-testid="script-30">
          <div className="text-[10px] uppercase tracking-widest text-[#0A8CF2] font-mono-tech">Vertical 9:16 works too — burn the captions in, most of it is watched muted</div>
          <div className="mt-1">{SCRIPT_30.map((c) => <Scene key={c.id} scene={c} />)}</div>
        </div>
      )}
    </section>
  );
};
